/**
 * O layout das artes — aritmética pura, sem canvas e sem DOM.
 *
 * Este módulo decide ONDE cada coisa vai. Quem desenha é
 * `src/ui/arte/desenho.ts`, que só executa o plano devolvido aqui.
 *
 * A separação existe por um motivo concreto: assim o layout roda em Node e
 * ganha teste de verdade. As coordenadas da arte são o produto — se o título
 * do template `passo` descer 8px, ninguém percebe olhando, e a peça sai torta
 * no perfil do cliente. Teste de canvas exigiria navegador; teste de
 * aritmética não exige nada.
 *
 * E há um detalhe que torna isso possível: **`fim` não depende de métrica de
 * fonte**. As quebras de linha são `\n` explícitos (nunca automáticas, ver
 * `linhas`), então a altura de um bloco é contagem × entrelinha. A única coisa
 * que precisa de `measureText` é a palavra em destaque do `manif`, e ela não
 * mexe em altura nenhuma.
 *
 * Nada aqui importa nada. Mesma regra de `src/domain/contratos.ts`: se este
 * arquivo precisar de um import, é sinal de que a responsabilidade vazou.
 */

// ---------------------------------------------------------------- tokens

export const COR = {
  navy: "#0E0E20",
  card: "#14142B",
  line: "#2A2A48",
  roxo: "#6C4FE0",
  lilas: "#A78BFA",
  texto: "#F4F4F8",
  apoio: "#C9C9DA",
  mute: "#8A8AA8"
} as const;

/**
 * As duas famílias. Os arquivos são servidos de `public/fontes/`, não do
 * Google Fonts: o CSP desta API é `'self'` puro (ver src/http/app.ts), e
 * abri-lo para um CDN de terceiro para buscar fonte seria pagar caro por
 * comodidade.
 */
export const FONTE = {
  sans: '"Space Grotesk"',
  mono: '"JetBrains Mono"'
} as const;

export const LARGURA = 1080;
export const ALTURA = 1350;
export const MARGEM = 72;
/** Largura útil entre as margens. */
export const UTIL = LARGURA - MARGEM * 2;

/**
 * O corte 1:1 da miniatura no perfil.
 *
 * O feed mostra 1080×1080 centralizado de uma arte 1080×1350. O que passa de
 * `SEGURO_BASE` existe no post aberto e some na grade — que é onde a maior
 * parte das pessoas vê.
 */
export const SEGURO_TOPO = 135;
export const SEGURO_BASE = 1215;

/**
 * A caixa onde o logo cabe, em qualquer modelo.
 *
 * Fixa de propósito: o logo é a assinatura da peça e precisa cair no mesmo
 * lugar em todas elas. O que varia é a MARCA, não a posição — senão a grade
 * do perfil vira um mosaico com o logo dançando de arte para arte.
 *
 * O PNG entra por `contain` (nunca `cover`): logo recortado é logo adulterado,
 * e marca de cliente não se recorta. Logo largo fica largo, logo quadrado fica
 * pequeno — é a consequência honesta de uma caixa só.
 */
export const LOGO_CAIXA = { x: 72, y: 152, w: 300, h: 52 } as const;

export const DY_MIN = -320;
export const DY_MAX = 320;

export type Template = "foto" | "manif" | "comp" | "passo";

/**
 * De onde vem o logo desta arte.
 *
 * Três estados, e nenhum deles é acidente:
 *
 *   "nenhum"  — não desenha. É o PADRÃO, e é a escolha segura: o editor serve
 *               todos os projetos, e uma arte da Geely que nasce com o logo do
 *               AntiFila é um erro que passa despercebido até ir ao ar.
 *   "vetor"   — o AntiFila desenhado a traço. Nítido em qualquer escala, e é
 *               o único logo que existia antes da biblioteca.
 *   number    — o id de um PNG em `logos`.
 */
export type OrigemLogo = "nenhum" | "vetor" | number;

// ---------------------------------------------------------------- modelo

/**
 * Uma arte.
 *
 * Todos os campos existem em todos os templates — trocar de template preserva
 * o que já foi escrito, e cada template simplesmente ignora o que não usa.
 * Isso é o que permite experimentar layout sem perder texto.
 */
export interface Arte {
  tpl: Template;
  /** Linha de cima, mono, caixa alta por convenção. */
  kicker: string;
  /** `\n` quebra linha. Nunca há quebra automática. */
  titulo: string;
  apoio: string;
  /** `manif`: a palavra do título que sai em lilás. */
  destaque: string;
  /** `manif`. */
  rodape: string;
  /** `comp`: coluna da esquerda. */
  rotuloA: string;
  textoA: string;
  /** `comp`: coluna da direita. */
  rotuloB: string;
  textoB: string;
  /** `passo`: '01'..'09'. */
  numero: string;
  /** `passo`: total de passos, 1..9. */
  total: number | string;
  /** `media_assets.id` da foto, ou null. */
  fotoA: number | null;
  /** `media_assets.id`; só `comp` usa. */
  fotoB: number | null;
  /** Deslocamento vertical do bloco de texto, -320..320. */
  dy: number;
  /** Qual marca assina a peça. Ver `OrigemLogo`. */
  logo: OrigemLogo;
}

export function arteVazia(): Arte {
  return {
    tpl: "foto",
    kicker: "",
    titulo: "",
    apoio: "",
    destaque: "",
    rodape: "",
    rotuloA: "",
    textoA: "",
    rotuloB: "",
    textoB: "",
    numero: "01",
    total: 3,
    fotoA: null,
    fotoB: null,
    dy: 0,
    // Nenhum, e não "vetor": ver OrigemLogo. Escolher a marca é decisão de
    // quem compõe, não um padrão herdado do primeiro projeto que existiu.
    logo: "nenhum"
  };
}

// ---------------------------------------------------------------- plano

export type Fonte = "sans" | "mono";

export interface Texto {
  tipo: "texto";
  x: number;
  /** Baseline da PRIMEIRA linha. `fillText` mede por baseline. */
  y: number;
  linhas: string[];
  tamanho: number;
  entrelinha: number;
  cor: string;
  peso: number;
  fonte: Fonte;
  /** Espaço extra por caractere, em px. 0 = normal. */
  tracking: number;
  alinhamento: "esquerda" | "direita";
  /**
   * `manif`: pinta esta palavra de lilás dentro da linha em que aparecer.
   * Exige `measureText`, e por isso é a única coisa aqui que o desenho
   * resolve sozinho.
   */
  destaque?: string;
}

export type Primitiva =
  | {
      tipo: "retangulo";
      x: number;
      y: number;
      w: number;
      h: number;
      cor: string;
    }
  | {
      /** Gradiente vertical, de `de` (topo) a `para` (base). */
      tipo: "gradiente";
      x: number;
      y: number;
      w: number;
      h: number;
      de: string;
      para: string;
    }
  | {
      tipo: "foto";
      slot: "A" | "B";
      x: number;
      y: number;
      w: number;
      h: number;
      /** O que o placeholder escreve quando não há foto no slot. */
      rotulo: string;
    }
  | {
      tipo: "linha";
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      cor: string;
      largura: number;
    }
  | {
      tipo: "logo";
      /** A caixa. O desenho encaixa a marca aqui dentro, por `contain`. */
      x: number;
      y: number;
      w: number;
      h: number;
      origem: "vetor" | number;
    }
  | {
      tipo: "pilula";
      x: number;
      y: number;
      w: number;
      h: number;
      raio: number;
      cor: string;
    }
  | Texto;

export interface Plano {
  primitivas: Primitiva[];
  /**
   * O `y` onde o último bloco de texto termina.
   *
   * Acima de `SEGURO_BASE` o texto invade o corte 1:1 da miniatura.
   */
  fim: number;
}

// ---------------------------------------------------------------- ajudantes

export function clampDy(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(DY_MAX, Math.max(DY_MIN, n));
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Quebra um campo em linhas.
 *
 * SEM quebra automática, de propósito: um título de 88px quebrado por largura
 * produz resultado imprevisível a cada troca de palavra, e quem escreve perde
 * o controle da composição. Quem decide onde quebra é quem escreve, com Enter.
 *
 * Campo vazio devolve lista vazia, e não `[""]` — senão um apoio em branco
 * contaria como uma linha e empurraria o `fim` para baixo sem nada desenhado.
 */
export function linhas(texto: string): string[] {
  if (texto.trim() === "") return [];
  return texto.split("\n");
}

/**
 * Baseline da ÚLTIMA linha de um bloco, ou `null` se o bloco está vazio.
 *
 * Bloco vazio não tem última linha, e devolver o `y` inicial faria o `fim`
 * contar um texto que não existe.
 */
function ultimaBaseline(
  y: number,
  n: number,
  entrelinha: number
): number | null {
  if (n === 0) return null;
  return y + (n - 1) * entrelinha;
}

function txt(
  p: Omit<Texto, "tipo" | "tracking" | "alinhamento"> &
    Partial<Pick<Texto, "tracking" | "alinhamento">>
): Texto {
  return {
    tipo: "texto",
    tracking: 0,
    alinhamento: "esquerda",
    ...p
  };
}

/**
 * O logo da arte, ou `null` quando ela não leva nenhum.
 *
 * Devolve `null` em vez de uma primitiva inerte de propósito: assim "arte sem
 * logo" não produz nada no plano, e um teste que conta primitivas não precisa
 * saber que existe um caso especial.
 */
function logoDe(a: Arte): Primitiva | null {
  if (a.logo === "nenhum") return null;
  return { tipo: "logo", ...LOGO_CAIXA, origem: a.logo };
}

/** Monta a lista descartando o que não existe nesta arte. */
function juntar(...itens: (Primitiva | null)[]): Primitiva[] {
  return itens.filter((p): p is Primitiva => p !== null);
}

// ---------------------------------------------------------------- templates

/**
 * `foto` — foto sangrada com faixa de texto embaixo.
 *
 * Único template em que `dy` move também a borda da foto, e não só o texto.
 */
function planoFoto(a: Arte, dy: number): Plano {
  const ph = clamp(900 + dy, 520, 1150);

  const nTitulo = linhas(a.titulo);
  const nApoio = linhas(a.apoio);

  const yTitulo = ph + 47;
  const yApoio = yTitulo + nTitulo.length * 92 + 44;

  const primitivas: Primitiva[] = juntar(
    { tipo: "foto", slot: "A", x: 0, y: 0, w: LARGURA, h: ph, rotulo: "FOTO" },
    // Os dois véus são contraste, não enfeite: o de cima protege o logo de uma
    // foto clara, o de baixo costura a foto com a faixa navy sem borda dura.
    {
      tipo: "gradiente",
      x: 0,
      y: 0,
      w: LARGURA,
      h: 360,
      de: "rgba(14,14,32,0.72)",
      para: "rgba(14,14,32,0)"
    },
    {
      tipo: "gradiente",
      x: 0,
      y: ph - 380,
      w: LARGURA,
      h: 380,
      de: "rgba(14,14,32,0)",
      para: COR.navy
    },
    {
      tipo: "retangulo",
      x: 0,
      y: ph,
      w: LARGURA,
      h: ALTURA - ph,
      cor: COR.navy
    },
    logoDe(a),
    txt({
      x: MARGEM,
      y: ph - 55,
      linhas: linhas(a.kicker),
      tamanho: 24,
      entrelinha: 34,
      cor: COR.lilas,
      peso: 400,
      fonte: "mono",
      tracking: 4.3
    }),
    txt({
      x: MARGEM,
      y: yTitulo,
      linhas: nTitulo,
      tamanho: 88,
      entrelinha: 92,
      cor: COR.texto,
      peso: 700,
      fonte: "sans"
    }),
    txt({
      x: MARGEM,
      y: yApoio,
      linhas: nApoio,
      tamanho: 30,
      entrelinha: 44,
      cor: COR.apoio,
      peso: 400,
      fonte: "sans"
    })
  );

  const fimApoio = ultimaBaseline(yApoio, nApoio.length, 44);
  const fimTitulo = ultimaBaseline(yTitulo, nTitulo.length, 92);
  // Sem apoio, quem termina o bloco é o título.
  const fim = (fimApoio ?? fimTitulo ?? yTitulo) + 10;

  return { primitivas, fim };
}

/**
 * `manif` — manifesto tipográfico, sem foto.
 *
 * O divisor e o rodapé são FIXOS: `dy` move só o bloco kicker + título. Eles
 * são a assinatura da peça, e uma assinatura que dança de arte para arte
 * deixa de ser assinatura.
 */
function planoManif(a: Arte, dy: number): Plano {
  const nTitulo = linhas(a.titulo);
  const nRodape = linhas(a.rodape);

  const yTitulo = 483 + dy;
  const yRodape = 1086;

  const primitivas: Primitiva[] = juntar(
    { tipo: "retangulo", x: 0, y: 0, w: LARGURA, h: ALTURA, cor: COR.navy },
    logoDe(a),
    txt({
      x: MARGEM,
      y: 355 + dy,
      linhas: linhas(a.kicker),
      tamanho: 24,
      entrelinha: 34,
      cor: COR.lilas,
      peso: 400,
      fonte: "mono",
      tracking: 4.3
    }),
    txt({
      x: MARGEM,
      y: yTitulo,
      linhas: nTitulo,
      tamanho: 104,
      entrelinha: 109,
      cor: COR.texto,
      peso: 700,
      fonte: "sans",
      destaque: a.destaque.trim() || undefined
    }),
    {
      tipo: "linha",
      x1: MARGEM,
      y1: 1020,
      x2: LARGURA - MARGEM,
      y2: 1020,
      cor: COR.line,
      largura: 1
    },
    txt({
      x: MARGEM,
      y: yRodape,
      linhas: nRodape,
      tamanho: 32,
      entrelinha: 44,
      cor: COR.apoio,
      peso: 400,
      fonte: "sans"
    })
  );

  const fimRodape = ultimaBaseline(yRodape, nRodape.length, 44);
  const fimTitulo = ultimaBaseline(yTitulo, nTitulo.length, 109);
  // O título sobe com `dy`, o rodapé não. Quem estoura pode ser qualquer um
  // dos dois, então o `fim` é o maior.
  const fim = Math.max(fimRodape !== null ? fimRodape + 10 : 0, fimTitulo ?? 0);

  return { primitivas, fim };
}

/**
 * `comp` — comparativo antes/depois.
 *
 * Os overlays são o que faz o lado direito "ser o AntiFila" sem rótulo
 * gráfico: o esquerdo apaga, o direito puxa para o roxo da marca.
 */
function planoComp(a: Arte, dy: number): Plano {
  const nA = linhas(a.textoA);
  const nB = linhas(a.textoB);
  const yTextos = 1118;

  const primitivas: Primitiva[] = juntar(
    { tipo: "retangulo", x: 0, y: 0, w: LARGURA, h: ALTURA, cor: COR.navy },
    logoDe(a),
    txt({
      x: MARGEM,
      y: 263 + dy,
      linhas: linhas(a.kicker),
      tamanho: 22,
      entrelinha: 30,
      cor: COR.lilas,
      peso: 400,
      fonte: "mono",
      tracking: 3.9
    }),
    txt({
      x: MARGEM,
      y: 339 + dy,
      linhas: linhas(a.titulo),
      tamanho: 66,
      entrelinha: 70,
      cor: COR.texto,
      peso: 700,
      fonte: "sans"
    }),
    { tipo: "foto", slot: "A", x: 0, y: 450, w: 540, h: 560, rotulo: "ANTES" },
    {
      tipo: "foto",
      slot: "B",
      x: 540,
      y: 450,
      w: 540,
      h: 560,
      rotulo: "DEPOIS"
    },
    {
      tipo: "retangulo",
      x: 0,
      y: 450,
      w: 540,
      h: 560,
      cor: "rgba(14,14,32,0.45)"
    },
    {
      tipo: "retangulo",
      x: 540,
      y: 450,
      w: 540,
      h: 560,
      cor: "rgba(108,79,224,0.22)"
    },
    txt({
      x: MARGEM,
      y: 1071,
      linhas: linhas(a.rotuloA),
      tamanho: 20,
      entrelinha: 28,
      cor: COR.mute,
      peso: 400,
      fonte: "mono",
      tracking: 3.2
    }),
    txt({
      x: 560,
      y: 1071,
      linhas: linhas(a.rotuloB),
      tamanho: 20,
      entrelinha: 28,
      cor: COR.lilas,
      peso: 400,
      fonte: "mono",
      tracking: 3.2
    }),
    txt({
      x: MARGEM,
      y: yTextos,
      linhas: nA,
      tamanho: 27,
      entrelinha: 39,
      cor: COR.mute,
      peso: 400,
      fonte: "sans"
    }),
    txt({
      x: 560,
      y: yTextos,
      linhas: nB,
      tamanho: 27,
      entrelinha: 39,
      cor: COR.texto,
      peso: 400,
      fonte: "sans"
    })
  );

  // A spec do MVP mede o `fim` SÓ pela coluna da direita. É defeito: uma lista
  // da esquerda mais longa estoura a zona segura sem disparar aviso nenhum.
  // Aqui o `fim` é o maior das duas colunas.
  const fimA = ultimaBaseline(yTextos, nA.length, 39);
  const fimB = ultimaBaseline(yTextos, nB.length, 39);
  const fim = Math.max(fimA ?? 0, fimB ?? 0, yTextos) + 8;

  return { primitivas, fim };
}

/** `passo` — passo de carrossel, sobre o fundo `card`. */
function planoPasso(a: Arte, dy: number): Plano {
  const nTitulo = linhas(a.titulo);
  const nApoio = linhas(a.apoio);

  const yTitulo = 700 + dy;
  const yApoio = yTitulo + nTitulo.length * 86 + 40;

  const total = Number.parseInt(String(a.total), 10);
  const n = clamp(Number.isFinite(total) ? total : 1, 1, 9);
  const feitos = Number.parseInt(a.numero, 10);
  const larguraSeg = (UTIL - (n - 1) * 10) / n;

  const barra: Primitiva[] = [];
  for (let i = 0; i < n; i++) {
    barra.push({
      tipo: "pilula",
      x: MARGEM + i * (larguraSeg + 10),
      y: 1180,
      w: larguraSeg,
      h: 6,
      raio: 3,
      cor: i < (Number.isFinite(feitos) ? feitos : 0) ? COR.lilas : COR.line
    });
  }

  const primitivas: Primitiva[] = juntar(
    { tipo: "retangulo", x: 0, y: 0, w: LARGURA, h: ALTURA, cor: COR.card },
    logoDe(a),
    txt({
      x: LARGURA - MARGEM,
      y: 189,
      linhas: [a.numero + " / " + String(a.total).padStart(2, "0")],
      tamanho: 22,
      entrelinha: 30,
      cor: COR.mute,
      peso: 400,
      fonte: "mono",
      alinhamento: "direita"
    }),
    txt({
      x: MARGEM,
      y: 528 + dy,
      linhas: [a.numero],
      tamanho: 170,
      entrelinha: 170,
      cor: COR.roxo,
      peso: 700,
      fonte: "mono"
    }),
    txt({
      x: MARGEM,
      y: yTitulo,
      linhas: nTitulo,
      tamanho: 76,
      entrelinha: 86,
      cor: COR.texto,
      peso: 700,
      fonte: "sans"
    }),
    txt({
      x: MARGEM,
      y: yApoio,
      linhas: nApoio,
      tamanho: 32,
      entrelinha: 46,
      cor: COR.apoio,
      peso: 400,
      fonte: "sans"
    }),
    // A barra é FIXA em y=1180. Ela é referência de navegação do carrossel:
    // tem que ficar na mesma altura em todos os slides, senão pula na troca.
    ...barra
  );

  const fimApoio = ultimaBaseline(yApoio, nApoio.length, 46);
  const fimTitulo = ultimaBaseline(yTitulo, nTitulo.length, 86);
  const fim = (fimApoio ?? fimTitulo ?? yTitulo) + 12;

  return { primitivas, fim };
}

// ---------------------------------------------------------------- entrada

/**
 * O plano de desenho de uma arte, com o `y` onde o texto termina.
 *
 * Mesma função para o preview e para a exportação — é isso que faz "o que se
 * vê é o que sai" ser verdade, e não promessa.
 */
export function plano(a: Arte): Plano {
  const dy = clampDy(a.dy);
  switch (a.tpl) {
    case "foto":
      return planoFoto(a, dy);
    case "manif":
      return planoManif(a, dy);
    case "comp":
      return planoComp(a, dy);
    case "passo":
      return planoPasso(a, dy);
  }
}

/**
 * Quantos px o texto passa da zona segura, ou `null` se cabe.
 *
 * É o número que a tela mostra e, no módulo, o que impede gerar a arte sem
 * alguém confirmar que sabe.
 */
export function estouro(a: Arte): number | null {
  const { fim } = plano(a);
  return fim > SEGURO_BASE ? Math.ceil(fim - SEGURO_BASE) : null;
}
